import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";

export default function DeleteQuoteDialog({ quote, open, onOpenChange, onDeleted }) {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  if (!quote) return null;
  const booked = !!(quote.booked || quote.gtz_bol_number || quote.bol_pro_number || quote.status === "booked");
  const qn = quote.quote_number || "";
  const canDelete = !booked || confirmText.trim().toUpperCase() === qn.toUpperCase();

  const close = (v) => {
    onOpenChange(v);
    if (!v) setConfirmText("");
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/quotes/${quote.id}`);
      toast.success(`${qn} permanently deleted`);
      close(false);
      onDeleted?.(quote);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={close}>
      <AlertDialogContent data-testid="delete-quote-dialog" className={booked ? "border-red-600/60" : ""}>
        <AlertDialogHeader>
          <AlertDialogTitle className={`flex items-center gap-2 ${booked ? "text-red-500" : ""}`}>
            {booked ? <AlertTriangle className="h-5 w-5" /> : <Trash2 className="h-5 w-5" />}
            {booked ? `Delete BOOKED shipment ${qn}?` : `Delete quote ${qn}?`}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              {booked ? (
                <>
                  <div className="rounded-md border border-red-600/50 bg-red-600/10 p-3 font-semibold text-red-400" data-testid="delete-booked-warning">
                    ⚠️ This load is BOOKED with a carrier. Deleting it only removes the record from Commander —
                    it does <span className="underline">NOT</span> cancel the carrier pickup or dispatch.
                    A truck may still show up. Cancel with the carrier first if the pickup shouldn't happen.
                  </div>
                  <p>
                    The quote, its BOL record, tracking link and all uploaded delivery documents will be
                    permanently erased. This cannot be undone.
                  </p>
                  <div>
                    <p className="mb-1.5 font-semibold text-foreground">Type <span className="metric-num text-red-400">{qn}</span> to confirm:</p>
                    <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={qn}
                      autoFocus data-testid="delete-confirm-input" className="border-red-600/40" />
                  </div>
                </>
              ) : (
                <p>
                  This permanently removes the quote, its history and any attached documents.
                  This cannot be undone.
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
          <Button variant="destructive" disabled={!canDelete || busy} onClick={doDelete} data-testid="delete-confirm-btn" className="gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {booked ? "Delete anyway" : "Delete"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
